import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { ArrowLeft, FileText, Loader2, CheckCircle, Home, ArrowRight, MapPin } from 'lucide-react';
import { ScopeGatheringStep } from '../../components/SOW/ScopeGatheringStep';
import { PropertyDataStep } from '../../components/SOW/PropertyDataStep';
import { ProjectType } from '../../models/sow';
import { useAuthToken } from '../../components/AuthTokenProvider';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';

enum WizardStep {
  ADDRESS = 'address',
  PROPERTY_CONFIRMATION = 'property',
  PROJECT_TYPE = 'project-type',
  COMPLETE = 'complete'
}

interface WizardStepInfo {
  key: WizardStep;
  title: string;
  description: string;
}

const WIZARD_STEPS: WizardStepInfo[] = [
  {
    key: WizardStep.ADDRESS,
    title: 'Property Address',
    description: 'Where is your renovation project located?'
  },
  {
    key: WizardStep.PROPERTY_CONFIRMATION,
    title: 'Home Details',
    description: 'Confirm details about your property'
  },
  {
    key: WizardStep.PROJECT_TYPE,
    title: 'Project Type',
    description: 'What type of renovation are you planning?'
  }
];

export const SOWGenerationWizard: React.FC = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();

  // Detect if we're in public mode (route starts with /scope instead of /sow)
  const isPublicMode = window.location.pathname.startsWith('/scope/');

  const [sow, setSOW] = useState<SOW | null>(null);
  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.ADDRESS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertyData, setPropertyData] = useState<any>(null);

  useEffect(() => {
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      loadSOW(sowId);
    }
  }, [sowId, tokenSet, tokenLoading, isPublicMode]);

  const loadSOW = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const sowData = await sowService.getSOW(id, !isPublicMode);
      setSOW(sowData);

      // Determine which step to show based on SOW state
      if (sowData.propertyData && sowData.address) {
        if (!sowData.scopeDefinition) {
          setCurrentStep(WizardStep.PROJECT_TYPE);
        } else {
          // Already complete, redirect to overview
          const overviewPath = isPublicMode ? `/scope/${id}` : `/sow/${id}`;
          navigate(overviewPath);
          return;
        }
        setPropertyData(sowData.propertyData);
      } else if (sowData.address) {
        setCurrentStep(WizardStep.PROPERTY_CONFIRMATION);
      } else {
        setCurrentStep(WizardStep.ADDRESS);
      }
    } catch (err) {
      console.error('Error loading SOW:', err);
      setError('Failed to load SOW. It may not exist or you may not have access.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressComplete = async (address: string) => {
    if (!sowId) return;
    
    try {
      // Update SOW with address
      await sowService.updateSOW(sowId, { address }, !isPublicMode);
      setSOW(prev => prev ? { ...prev, address } : null);
      setCurrentStep(WizardStep.PROPERTY_CONFIRMATION);
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address. Please try again.');
    }
  };

  const handlePropertyConfirmation = async (data: any) => {
    if (!sowId) return;
    
    try {
      // Update SOW with property data
      await sowService.updateSOW(sowId, { propertyData: data }, !isPublicMode);
      setPropertyData(data);
      setSOW(prev => prev ? { ...prev, propertyData: data } : null);
      setCurrentStep(WizardStep.PROJECT_TYPE);
    } catch (error) {
      console.error('Error saving property data:', error);
      setError('Failed to save property data. Please try again.');
    }
  };

  const handleProjectTypeSelected = async (projectType: ProjectType) => {
    if (!sowId) return;

    try {
      // Update SOW with scope definition
      const scopeDefinition = {
        projectType,
        components: [],
        finishLevel: 'Mid-range' as const,
      };

      await sowService.updateSOW(sowId, {
        scopeDefinition,
        status: 'initialQuestionnaire'
      }, !isPublicMode);

      // Redirect to overview page (public or authenticated based on mode)
      const overviewPath = isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`;
      navigate(overviewPath);
    } catch (error) {
      console.error('Error saving project type:', error);
      setError('Failed to save project type. Please try again.');
    }
  };

  const handleDescriptionProvided = async (description: string) => {
    if (!sowId) return;

    try {
      // Call backend to analyze description
      const analysis = await sowService.analyzeProjectDescription(sowId, description, !isPublicMode);

      // Backend automatically saves projectType and components to SOW
      // Redirect to combined checklist + photo page
      const questionnairePath = isPublicMode ? `/scope/${sowId}/questionnaire` : `/sow/${sowId}/questionnaire`;
      navigate(questionnairePath);
    } catch (error) {
      console.error('Error analyzing description:', error);
      setError('Failed to analyze project description. Please try again.');
      throw error; // Re-throw so the component shows error state
    }
  };

  const getCurrentStepIndex = () => {
    return WIZARD_STEPS.findIndex(step => step.key === currentStep);
  };

  const handleBackToSOWs = () => {
    navigate('/sow');
  };

  if (loading || (!isPublicMode && (tokenLoading || !tokenSet))) {
    return (
      <div className={`${isPublicMode ? 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100' : 'container mx-auto'} p-6`}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading setup wizard...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sow) {
    return (
      <div className="container mx-auto p-6">
        <Card className="text-center py-12 max-w-2xl mx-auto">
          <CardContent className="pt-6">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Unable to Load Project</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleBackToSOWs}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBackToSOWs}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        
      </div>


      {/* Step Content */}
      <div className="min-h-96">
        {currentStep === WizardStep.ADDRESS && (
          <AddressStep 
            onAddressComplete={handleAddressComplete}
            initialAddress={sow.address}
          />
        )}

        {currentStep === WizardStep.PROPERTY_CONFIRMATION && sow.address && (
          <PropertyDataStep
            address={sow.address}
            initialData={propertyData}
            onSave={handlePropertyConfirmation}
          />
        )}

        {currentStep === WizardStep.PROJECT_TYPE && sow.address && (
          <ScopeGatheringStep
            address={sow.address}
            onProjectTypeSelected={handleProjectTypeSelected}
            onDescriptionProvided={handleDescriptionProvided}
          />
        )}
      </div>
    </div>
  );
};

// Address Step Component
const AddressStep: React.FC<{
  onAddressComplete: (address: string) => void;
  initialAddress?: string;
}> = ({ onAddressComplete, initialAddress }) => {
  const [selectedAddress, setSelectedAddress] = useState(initialAddress || '');
  const [loading, setLoading] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const handleAddressSelected = async (address: string) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const geocodeResults = await getGeocode({ address });
      if (geocodeResults.length > 0) {
        const addressComponents = geocodeResults[0].address_components;

        const streetNumber = addressComponents.find((c: any) => c.types.includes('street_number'))?.long_name;
        const route = addressComponents.find((c: any) => c.types.includes('route'))?.long_name;
        const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name;
        const state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name;
        const zip = addressComponents.find((c: any) => c.types.includes('postal_code'))?.long_name;

        // Build complete address with zip
        const line1 = `${streetNumber || ''} ${route || ''}`.trim();
        const completeAddress = `${line1}, ${city || ''}, ${state || ''} ${zip || ''}`.trim();

        setSelectedAddress(completeAddress);
      } else {
        // Fallback to original address
        setSelectedAddress(address);
      }
    } catch (error) {
      // Fallback to original address if geocoding fails
      setSelectedAddress(address);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAddress.trim()) return;

    setLoading(true);
    try {
      // Simulate address validation/geocoding
      await new Promise(resolve => setTimeout(resolve, 500));
      onAddressComplete(selectedAddress.trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Property Address
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-gray-600 mb-4">
            Enter the address where your renovation project will take place. We'll use this to gather property information and provide accurate estimates.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Property Address</label>

              {!manualEntry ? (
                <>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10" />
                    <input
                      type="text"
                      className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Start typing an address..."
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && selectedAddress && handleSubmit()}
                      disabled={!ready}
                    />
                  </div>

                  {/* Address Suggestions */}
                  {status === 'OK' && (
                    <div className="mt-2 border rounded-md shadow-sm overflow-hidden bg-white">
                      {data.map((suggestion: any) => (
                        <div
                          key={suggestion.place_id}
                          onClick={() => handleAddressSelected(suggestion.description)}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="flex items-start">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm">{suggestion.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedAddress && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">Selected: {selectedAddress}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setManualEntry(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Can't find your address? Enter manually
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main Street, City, State 12345"
                    value={selectedAddress}
                    onChange={(e) => setSelectedAddress(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                  />
                  <button
                    type="button"
                    onClick={() => setManualEntry(false)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Use address search instead
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!selectedAddress.trim() || loading}
            className="min-w-32"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};